import React from 'react';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaTwitter, FaYoutube, FaInstagram } from 'react-icons/fa';
import { SITE_CONTENT } from '../content';

const iconMap = {
  facebook: FaFacebookF,
  twitter: FaTwitter,
  youtube: FaYoutube,
  instagram: FaInstagram,
};

const Footer = () => {
  const { logoText, logoImage } = SITE_CONTENT.global;
  const { 
    brandText, navLinks, socials, address, contactInfo, bookTable, bottomDocs 
  } = SITE_CONTENT.global.footer;

  return (
    <footer className="bg-[#071208] text-white">
      {/* Top Section */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-12 text-center border-b border-white/10">
        <Link to="/" className="inline-block mb-6">
          <img 
            src={logoImage} 
            alt={logoText} 
            className="h-20 sm:h-24 w-auto object-contain mx-auto rounded-full bg-white p-1" 
          />
        </Link>
        <p className="text-white/50 text-sm leading-relaxed max-w-md mx-auto mb-8">
          {brandText}
        </p>
        <nav className="flex flex-wrap justify-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className="text-white/60 text-xs tracking-[0.15em] uppercase hover:text-white transition-colors duration-200"
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Middle Section */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 border-b border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Address */}
          <div>
            <h3 className="text-xs tracking-[0.25em] uppercase text-white mb-5 font-semibold">
              {address.title}
            </h3>
            <p className="text-white/55 text-sm leading-relaxed mb-6">
              {address.lines.map((line, idx) => (
                <React.Fragment key={idx}>
                  {line}
                  {idx < address.lines.length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
            <div className="flex gap-3">
              {socials.map((social) => {
                const Icon = iconMap[social.id];
                if (!Icon) return null;
                return (
                  <a
                    key={social.id}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-8 h-8 rounded-full border border-white/30 flex items-center justify-center text-white/60 hover:text-white hover:border-white hover:bg-white/10 transition-all duration-200"
                  >
                    <Icon size={13} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xs tracking-[0.25em] uppercase text-white mb-5 font-semibold">
              {contactInfo.title}
            </h3>
            <div className="space-y-3 text-white/55 text-sm">
              <div className="space-y-1.5">
                {contactInfo.phones?.map((phone, idx) => {
                  const isObj = typeof phone === 'object' && phone !== null;
                  const displayText = isObj 
                    ? `${phone.number || phone.raw} - ${phone.name}` 
                    : phone;
                  const telHref = isObj 
                    ? (phone.raw || phone.number?.replace(/\s+/g, '')) 
                    : phone.replace(/[^0-9+]/g, '');

                  return (
                    <div key={idx} className="flex items-start">
                      <span className="text-white/70 font-medium w-7 shrink-0">
                        {idx === 0 ? 'P :' : ''}
                      </span>
                      <a
                        href={`tel:${telHref}`}
                        className="hover:text-white transition-colors duration-200"
                      >
                        {displayText}
                      </a>
                    </div>
                  );
                })}
              </div>
              <div className="pt-1 space-y-1.5">
                {contactInfo.emails?.map((email, idx) => (
                  <div key={idx} className="flex items-start">
                    <span className="text-white/70 font-medium w-7 shrink-0">
                      {idx === 0 ? 'E :' : ''}
                    </span>
                    <a
                      href={`mailto:${email}`}
                      className="hover:text-white transition-colors duration-200 break-all"
                    >
                      {email}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Book a Table */}
          <div>
            <h3 className="text-xs tracking-[0.25em] uppercase text-white mb-5 font-semibold">
              {bookTable.title}
            </h3>
            <p className="text-white/55 text-sm leading-relaxed mb-6">
              {bookTable.text}
            </p>
            <Link
              to={bookTable.buttonLink}
              className="inline-block px-6 py-2.5 border border-white/60 text-white text-xs tracking-[0.18em] uppercase rounded-full hover:bg-white hover:text-[#071208] transition-all duration-300"
            >
              {bookTable.buttonText}
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-white/35 text-xs">
          Copyright &copy; {new Date().getFullYear()} {logoText}. All rights reserved.
        </p>
        <div className="flex gap-6">
          {bottomDocs.map((doc) => (
            <Link
              key={doc.label}
              to={doc.to}
              className="text-white/35 text-xs hover:text-white/70 transition-colors duration-200"
            >
              {doc.label}
            </Link>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;